import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Plus, X, Users } from 'lucide-react';

// ── Types & mock data ────────────────────────────────────────────────────────

type WorkflowMode = 'finance-only' | 'pm-only' | 'finance-and-pm';

interface Reviewer {
  id: string;
  name: string;
}

const WORKFLOW_OPTIONS: { value: WorkflowMode; label: string; description: string }[] = [
  {
    value: 'finance-only',
    label: 'Finance Only',
    description:
      'Forecast Reviewer proposes and applies directly. No Project Manager review step.',
  },
  {
    value: 'pm-only',
    label: 'Project Manager Only',
    description:
      'Project Manager reviews unspent costs and applies directly. No Forecast Reviewer step.',
  },
  {
    value: 'finance-and-pm',
    label: 'Finance + Project Manager',
    description:
      'Forecast Reviewer proposes → Project Manager reviews/challenges → Forecast Reviewer resolves → applies.',
  },
];

const MOCK_INITIAL: { rollForwardWorkflowMode: WorkflowMode; forecastReviewers: Reviewer[] } = {
  rollForwardWorkflowMode: 'finance-only',
  forecastReviewers: [],
};

const MOCK_AVAILABLE_USERS: Reviewer[] = [
  { id: 'u1', name: 'Alice Finance' },
  { id: 'u2', name: 'Bob Controller' },
  { id: 'u3', name: 'Carol CFO' },
];

// ── Handle ───────────────────────────────────────────────────────────────────

export interface ForecastSettingsHandle {
  save: () => Promise<void>;
  discard: () => void;
}

interface ForecastSettingsTabProps {
  onDirtyChange?: (dirty: boolean) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export const ForecastSettingsTab = forwardRef<ForecastSettingsHandle, ForecastSettingsTabProps>(
  function ForecastSettingsTab({ onDirtyChange }, ref) {
    // Saved (persisted) state
    const [savedMode, setSavedMode] = useState<WorkflowMode>(MOCK_INITIAL.rollForwardWorkflowMode);
    const [savedReviewers, setSavedReviewers] = useState<Reviewer[]>(MOCK_INITIAL.forecastReviewers);

    // Working (in-flight) state
    const [mode, setMode] = useState<WorkflowMode>(MOCK_INITIAL.rollForwardWorkflowMode);
    const [reviewers, setReviewers] = useState<Reviewer[]>(MOCK_INITIAL.forecastReviewers);

    // UI state
    const [addOpen, setAddOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<Reviewer | null>(null);

    // Dirty tracking
    const isDirty =
      mode !== savedMode ||
      JSON.stringify([...reviewers].map((r) => r.id).sort()) !==
        JSON.stringify([...savedReviewers].map((r) => r.id).sort());

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Expose handle to parent (settings-project save/cancel bar)
    useImperativeHandle(ref, () => ({
      save: async () => {
        setSavedMode(mode);
        setSavedReviewers([...reviewers]);
      },
      discard: () => {
        setMode(savedMode);
        setReviewers([...savedReviewers]);
      },
    }));

    const addReviewer = (user: Reviewer) => {
      setReviewers((prev) => [...prev, user]);
      setAddOpen(false);
    };

    const confirmRemove = () => {
      if (!removeTarget) return;
      setReviewers((prev) => prev.filter((r) => r.id !== removeTarget.id));
      setRemoveTarget(null);
    };

    const available = MOCK_AVAILABLE_USERS.filter(
      (u) => !reviewers.some((r) => r.id === u.id),
    );

    const initials = (name: string) =>
      name
        .split(' ')
        .map((p) => p[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return (
      <div className="space-y-8">

        {/* ── Section 1: Roll-Forward Workflow Mode ─────────────────────── */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Roll-Forward Workflow Mode</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Controls how unspent costs are reviewed and applied during a forecast roll-forward.
            </p>
          </div>

          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as WorkflowMode)}
            className="space-y-2"
          >
            {WORKFLOW_OPTIONS.map((opt) => {
              const isSelected = mode === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`wf-${opt.value}`}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={`wf-${opt.value}`}
                    className="mt-0.5 shrink-0"
                  />
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{opt.label}</span>
                      {opt.value === 'finance-only' && (
                        <Badge variant="secondary" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </div>

        <Separator />

        {/* ── Section 2: Forecast Reviewers ──────────────────────────────── */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Forecast Reviewers</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Users who receive roll-forward notifications and can propose or apply forecast changes.
            </p>
          </div>

          {reviewers.length === 0 ? (
            <div className="flex items-center gap-3 rounded-lg border border-dashed p-4 text-muted-foreground">
              <Users className="h-4 w-4 shrink-0" />
              <p className="text-sm">
                No Forecast Reviewers assigned. Roll-Forward notifications will go to Administrators.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {reviewers.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-1.5 rounded-full border bg-muted/40 pl-1 pr-2 py-1"
                >
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-[10px]">{initials(r.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{r.name}</span>
                  <button
                    type="button"
                    onClick={() => setRemoveTarget(r)}
                    className="text-muted-foreground hover:text-foreground transition-colors ml-0.5"
                    aria-label={`Remove ${r.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Reviewer popover */}
          <div className="flex items-center gap-3">
            <Popover open={addOpen} onOpenChange={setAddOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={available.length === 0}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Reviewer
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search users…" />
                  <CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                    {available.map((u) => (
                      <CommandItem key={u.id} onSelect={() => addReviewer(u)}>
                        <Avatar className="h-5 w-5 mr-2">
                          <AvatarFallback className="text-[10px]">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        {u.name}
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {reviewers.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {reviewers.length} Forecast Reviewer{reviewers.length !== 1 ? 's' : ''} assigned
              </p>
            )}
          </div>
        </div>

        {/* Remove confirmation dialog */}
        <AlertDialog
          open={!!removeTarget}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Forecast Reviewer?</AlertDialogTitle>
              <AlertDialogDescription>
                Remove <strong>{removeTarget?.name}</strong> as a Forecast Reviewer? They will no
                longer receive roll-forward notifications.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmRemove}>Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  },
);
